<template>
  <div
    class="w-full rounded-lg flex justify-between items-center bg-black-card relative"
    :class="{ 'h-13': !compact, 'px-5': !compact }"
    @mousemove="() => (hover = true)"
    @mouseleave="() => (hover = false)"
  >
    <div
      class="text-sm flex-1 flex-shrink-0 text-white/70 flex items-center"
      :class="{
        ' !text-white/30': disabled,
        'cursor-ew-resize': !disabled && labelDraggable
      }"
      @mousedown="handleDragStart"
    >
      {{ label }}
      <a-tooltip v-if="tooltip.length" :title="tooltip">
        <icon
          class="ml-1 !cursor-default"
          :class="{
            'opacity-60': disabled
          }"
          icon="help.svg"
          :width="14"
          :height="14"
          fill="none"
        />
      </a-tooltip>
    </div>
    <div
      class="flex justify-end items-center relative"
      :style="{
        flex: contentFlex
      }"
    >
      <slot :hover="hover" />
    </div>
    <!-- 全屏范围显示拖拽光标 -->
    <Teleport to="#app">
      <div
        v-if="labelDragging"
        class="fixed inset-0 cursor-ew-resize"
        style="z-index: 9999"
      ></div
    ></Teleport>
  </div>
</template>
<script lang="ts" setup>
import { useMouse, useMousePressed } from '@vueuse/core';
import { ref, watch } from 'vue';

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: undefined
  },
  compact: {
    type: Boolean,
    required: false,
    default: false
  },
  labelDraggable: {
    type: Boolean,
    required: false,
    default: false
  },
  labelDragUnit: {
    type: Number,
    default: 4
  },
  labelDragStep: {
    type: Number,
    default: 1
  },
  tooltip: {
    type: String,
    default: ''
  },
  contentFlex: {
    type: Number,
    default: 1
  }
});

const emit = defineEmits<{
  (event: 'labelDrag', value: { x: number; y: number }): void;
  (event: 'labelDragEnd'): void;
}>();

// 标签拖拽
const { x: mouseX, y: mouseY } = useMouse();
const { pressed: mousePressed } = useMousePressed();
const labelDragging = ref(false);
const debouncedX = ref(0),
  debouncedY = ref(0);
const startX = ref(0),
  startY = ref(0);
// 鼠标抬起 结束拖拽状态
watch(
  () => mousePressed.value,
  (newVal) => {
    if (!newVal) labelDragging.value = false;
    emit('labelDragEnd');
  }
);
// 鼠标移动 在拖拽状态则处理
watch(
  () => ({ x: mouseX.value, y: mouseY.value }),
  (newVal) => {
    if (!labelDragging.value) return;
    const dx =
      Math.floor((newVal.x - startX.value) / props.labelDragUnit) *
      props.labelDragStep;
    const dy =
      Math.floor((newVal.y - startY.value) / props.labelDragUnit) *
      props.labelDragStep;
    // 数值防抖
    if (dx === debouncedX.value && dy === debouncedY.value) return;
    [debouncedX.value, debouncedY.value] = [dx, dy];
    emit('labelDrag', { x: dx, y: dy });
  }
);
// 鼠标按下 开始拖拽状态并记录初始鼠标位置
const handleDragStart = () => {
  if (props.disabled || !props.labelDraggable) return;
  [startX.value, startY.value, labelDragging.value] = [
    mouseX.value,
    mouseY.value,
    true
  ];
};

const hover = ref(false);
</script>

<style lang="less" scoped></style>
